import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PLAN_PRICING = {
  starter: 950,
  growth: 1500,
  partner: 2500
};

export default function ConvertToClientModal({ lead, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    plan_tier: 'starter',
    monthly_fee: PLAN_PRICING.starter,
    start_date: new Date().toISOString().split('T')[0],
    billing_notes: '',
    assigned_staff_username: '',
    initial_note: ''
  });
  const [useCustomFee, setUseCustomFee] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchStaffMembers();
    }
  }, [isOpen]);

  const fetchStaffMembers = async () => {
    try {
      const response = await fetch(`${API}/api/admin/staff-members`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const activeStaff = data.staff_members.filter(s => s.is_active);
        setStaffMembers(activeStaff);
        
        // Set default to Onboarding Agent
        const onboardingAgent = activeStaff.find(s => s.role === 'Onboarding');
        if (onboardingAgent) {
          setFormData(prev => ({ ...prev, assigned_staff_username: `${onboardingAgent.first_name} ${onboardingAgent.last_name}` }));
        }
      }
    } catch (error) {
      console.error('Error fetching staff members:', error);
    }
  };

  const handlePlanChange = (plan) => {
    setFormData({ 
      ...formData, 
      plan_tier: plan,
      monthly_fee: useCustomFee ? formData.monthly_fee : PLAN_PRICING[plan]
    });
  };

  const handleCustomFeeToggle = (checked) => {
    setUseCustomFee(checked);
    if (!checked) {
      // Reset to plan default
      setFormData({ ...formData, monthly_fee: PLAN_PRICING[formData.plan_tier] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.monthly_fee || parseFloat(formData.monthly_fee) <= 0) {
      setError('Monthly fee must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/leads/${lead.id}/convert-to-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          monthly_fee: parseFloat(formData.monthly_fee)
        })
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data.short_id || data.client_id);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to convert lead to client');
      }
    } catch (error) {
      console.error('Error converting lead:', error);
      setError('Failed to convert lead to client');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Convert Lead to Client</h2>
              <p className="text-sm text-gray-600 mt-1">
                Converting: <span className="font-medium">{lead.first_name} {lead.last_name}</span> - {lead.company || lead.company_name}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Lead Info Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-blue-900 text-sm">Lead Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-blue-700 font-medium">Business:</span>
                  <p className="text-blue-900">{lead.company || lead.company_name || '-'}</p>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Contact:</span>
                  <p className="text-blue-900">{lead.first_name} {lead.last_name}</p>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Email:</span>
                  <p className="text-blue-900">{lead.email}</p>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Phone:</span>
                  <p className="text-blue-900">{lead.phone}</p>
                </div>
                {lead.service_type && (
                  <div>
                    <span className="text-blue-700 font-medium">Service Type:</span>
                    <p className="text-blue-900 capitalize">{lead.service_type.replace('_', ' ')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Client Setup Fields */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-gray-900">Client Setup</h3>
              
              <div>
                <Label htmlFor="plan_tier">Plan Tier *</Label>
                <Select value={formData.plan_tier} onValueChange={handlePlanChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter - $950/mo</SelectItem>
                    <SelectItem value="growth">Growth - $1,500/mo</SelectItem>
                    <SelectItem value="partner">Partner - $2,500/mo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="monthly_fee">Monthly Fee *</Label>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={useCustomFee}
                      onChange={(e) => handleCustomFeeToggle(e.target.checked)}
                      className="rounded"
                    />
                    Use custom fee
                  </label>
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="monthly_fee"
                    type="number"
                    step="0.01"
                    value={formData.monthly_fee}
                    onChange={(e) => setFormData({ ...formData, monthly_fee: e.target.value })}
                    className="pl-7"
                    readOnly={!useCustomFee}
                    required
                  />
                </div>
                {!useCustomFee && (
                  <p className="text-xs text-gray-500 mt-1">Auto-populated based on plan tier</p>
                )}
              </div>

              <div>
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="assigned_staff">Assigned Staff / Project Manager *</Label>
                <Select 
                  value={formData.assigned_staff_username} 
                  onValueChange={(value) => setFormData({ ...formData, assigned_staff_username: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staffMembers.map((staff) => (
                      <SelectItem key={staff.staff_member_id} value={`${staff.first_name} ${staff.last_name}`}>
                        {staff.first_name} {staff.last_name} - {staff.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="billing_notes">Billing Notes (optional)</Label>
                <Textarea
                  id="billing_notes"
                  value={formData.billing_notes}
                  onChange={(e) => setFormData({ ...formData, billing_notes: e.target.value })}
                  placeholder="Any special billing arrangements..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="initial_note">Initial Note (optional)</Label>
                <Textarea
                  id="initial_note"
                  value={formData.initial_note}
                  onChange={(e) => setFormData({ ...formData, initial_note: e.target.value })}
                  placeholder="Add an initial note to the client record..."
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? 'Converting...' : (
                  <>
                    Convert to Client
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
